import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { LoadingLogo } from './LoadingScreen';
import { fmt } from '../utils/formatters';

/**
 * The Time Machine reveal as a modal — the "had you been on ClearPath, it
 * would have found you $X" moment — for owners who bring their REAL data
 * after onboarding: a CSV on Settings › Data, or a first POS sync on
 * Settings › Connections. Onboarding keeps its own full-screen version of
 * the same moment; this one interrupts a page they are already on, so it
 * stays small, says one thing, and has a way out.
 *
 * Copy discipline (the 09-08 honesty rules): the engine estimates what it
 * would have FOUND — opportunities with a dollar range — never what the
 * owner would have made or saved. Every surface uses "found"; so does this.
 */

export interface TimeMachineReport {
  total_found_point: number;
  total_found_low: number;
  total_found_high: number;
  weeks_analyzed: number;
  start_date: string; // ISO date
  end_date: string;   // ISO date
}

type RevealState = 'idle' | 'running' | 'done';

// framer-motion 11 types AnimatePresence's return as `Element | undefined`,
// which React 18's JSX namespace rejects. Same narrowing OnboardingPage uses.
const Presence = AnimatePresence as unknown as React.FC<{ children?: React.ReactNode }>;

/** Counts to a value once, then holds. Static under reduced motion. */
export function useCountUp(target: number | null, enabled: boolean, ms = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target == null) return;
    if (!enabled) { setValue(target); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      // Exponential ease-out: fast commitment, unhurried settle.
      setValue(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, enabled, ms]);
  return value;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/** "March 23" / "September 14, 2026" — year only where it disambiguates. */
export function formatSpan(startIso: string, endIso: string): { start: string; end: string } {
  const parse = (iso: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) } : null;
  };
  const s = parse(startIso);
  const e = parse(endIso);
  if (!s || !e) return { start: startIso, end: endIso };
  const sameYear = s.y === e.y;
  const start = `${MONTHS[s.mo - 1]} ${s.d}${sameYear ? '' : `, ${s.y}`}`;
  const end = `${MONTHS[e.mo - 1]} ${e.d}, ${e.y}`;
  return { start, end };
}

/**
 * Owns the run → poll → show sequence. `reveal()` re-runs the backtest on
 * whatever is in the account now and opens the modal only when there is
 * something worth revealing; a zero-result closes silently rather than
 * manufacturing a moment (same rule onboarding follows).
 */
export function useTimeMachineReveal() {
  const [state, setState] = useState<RevealState>('idle');
  const [report, setReport] = useState<TimeMachineReport | null>(null);

  const reveal = useCallback(async () => {
    setState('running');
    let rpt: TimeMachineReport | null = null;
    try {
      rpt = (await axios.post('/api/timemachine/run')).data?.report ?? null;
    } catch { /* fall through to polling — the background run may finish first */ }
    if (!(rpt && rpt.total_found_point > 0)) {
      for (let i = 0; i < 10 && !(rpt && rpt.total_found_point > 0); i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try { rpt = (await axios.get('/api/timemachine')).data?.report ?? null; } catch { /* keep polling */ }
      }
    }
    if (rpt && rpt.total_found_point > 0) {
      setReport(rpt);
      setState('done');
    } else {
      setState('idle');
    }
  }, []);

  const close = useCallback(() => setState('idle'), []);

  return { state, report, reveal, close };
}

interface ModalProps {
  state: RevealState;
  report: TimeMachineReport | null;
  onClose: () => void;
}

export default function TimeMachineRevealModal({ state, report, onClose }: ModalProps) {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const primaryRef = useRef<HTMLButtonElement>(null);

  const found = state === 'done' && report ? report.total_found_point : null;
  const counted = useCountUp(found, !reduce);

  // Escape closes; focus lands on the one action once the number is up.
  useEffect(() => {
    if (state === 'idle') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, onClose]);
  useEffect(() => {
    if (state === 'done') primaryRef.current?.focus();
  }, [state]);

  const span = report ? formatSpan(report.start_date, report.end_date) : null;

  return (
    <Presence>
      {state !== 'idle' && (
        <motion.div
          key="tm-reveal"
          initial={reduce ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduce ? {} : { opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-[#2D2A2E]/40 backdrop-blur-[3px]"
          onClick={state === 'done' ? onClose : undefined}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="tm-reveal-title"
            initial={reduce ? {} : { opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? {} : { opacity: 0, y: 10, scale: 0.99 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {state === 'running' || !report || !span ? (
              <div className="bg-[#4A2D5E] rounded-[14px] px-8 py-14 text-center shadow-[0_18px_48px_rgba(74,45,94,0.28)]">
                <div className="mx-auto w-fit"><LoadingLogo /></div>
                <h2 id="tm-reveal-title" className="mt-6 font-serif text-2xl text-white">Reading your history…</h2>
                <p className="mt-2 font-sans text-sm text-white/70">
                  Replaying every detector over every sale you just brought in.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-[14px] border border-[#E6E2DE] px-8 pt-10 pb-8 shadow-[0_18px_48px_rgba(74,45,94,0.18)]">
                <p id="tm-reveal-title" className="font-sans text-sm text-[#6B6670] text-center leading-relaxed">
                  Had you been on ClearPath from <span className="text-[#2D2A2E] font-medium">{span.start}</span> to{' '}
                  <span className="text-[#2D2A2E] font-medium">{span.end}</span>, it would have found you
                </p>
                <div className="mt-3 text-center font-serif text-[64px] leading-none tabular-nums text-[#2E6B4F]">
                  {fmt(counted)}
                </div>
                <p className="mt-3 font-sans text-sm text-[#6B6670] text-center">
                  {fmt(report.total_found_low)}–{fmt(report.total_found_high)} across{' '}
                  {report.weeks_analyzed} {report.weeks_analyzed === 1 ? 'week' : 'weeks'} — estimated, from your own sales.
                </p>

                <div className="mt-8 flex flex-col gap-2">
                  <button
                    ref={primaryRef}
                    onClick={() => { onClose(); navigate('/home'); }}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#4A2D5E] text-white hover:bg-[#3D254D] focus:outline-none focus:ring-2 focus:ring-[#4A2D5E]/30 rounded-[8px] h-11 font-sans font-medium text-sm transition-colors"
                  >
                    See what it found
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full h-10 font-sans text-sm text-[#6B6670] hover:text-[#2D2A2E] focus:outline-none focus:ring-2 focus:ring-[#4A2D5E]/20 rounded-[8px] transition-colors"
                  >
                    Not now
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </Presence>
  );
}
